// CSR-2286 / CSR-2250 — "does this policy carry real COI coverage?", lifted out
// of `server/services/coiTemplate.ts` so the Send / Resend COI policy picker can
// ask the same question in the browser that the render path asks on the server.
// A second copy would drift, and the drift would show as a certificate offering
// a policy the render then drops. The predicate and the primitives it is built
// from move together for that reason; `coiTemplate.ts` imports them back.
//
// Pure coverage helpers shared by the browser and server, with no server imports.
import type { CoiAutoLiabilityBlock } from "./coi-template";
import { getMaxPhysicalDamageDeductibles } from "./physical-damage-tiers";
import { STANDARD_AUTO_LIABILITY_LIMIT_KEYS } from "./coverage-rules";
import type {
  PhysicalDamageVehicleSelection,
  Policy,
  PolicyCoverages,
  PolicyVehicle,
} from "./schema";

// Treat empty strings, nullish, and "0" (the DC sentinel for absent — Progressive
// often emits "0" when a coverage line is declined) as no value. Used to skip
// emitting Other Coverages rows whose every numeric field collapses to absent.
export const hasValue = (v: unknown): boolean => {
  if (v == null) return false;
  const s = String(v).trim();
  if (!s) return false;
  return !/^0+(\.0+)?$/.test(s);
};

export function selectPipCoverage(al: CoiAutoLiabilityBlock): { label: string; limit: string } | undefined {
  if (hasValue(al.pipLimit)) return { label: "PIP", limit: al.pipLimit!.trim() };
  if (hasValue(al.basicPipLimit)) return { label: "PIP-Basic", limit: al.basicPipLimit!.trim() };
  return undefined;
}

// CSR-679: a coverage with `status === "removed"` was on the carrier policy in
// a prior sync but is no longer active. AMS retains the row for audit, but it
// must be excluded from any COI output. Treat such blocks as absent here.
export const isCoverageActive = (block: Record<string, unknown> | string | undefined): boolean => {
  if (!block || typeof block === "string") return !!block;
  return (block as Record<string, unknown>).status !== "removed";
};

export const activeBlock = <T extends Record<string, unknown> | string | undefined>(block: T): T | undefined =>
  isCoverageActive(block) ? block : undefined;

// CSR-775: `activeBlock` only filters on `status === "removed"`, but the DC
// sync sometimes emits coverage blocks with the shape present and every limit
// field empty. Gate the AcroForm-backed GL/AL/UB/WC sections on whether at
// least one *coverage limit* field carries a real value via `hasValue`.
export const hasAnyFieldValue = (
  block: Record<string, unknown> | string | undefined,
  keys: string[],
): boolean => {
  if (block == null) return false;
  if (typeof block === "string") return hasValue(block);
  for (const k of keys) {
    if (hasValue((block as Record<string, unknown>)[k])) return true;
  }
  return false;
};
export const GL_VALUE_KEYS = [
  "eachOccurrence",
  "damageToRented",
  "medExp",
  "personalAdvInjury",
  "generalAggregate",
  "productsCompOpAgg",
];
export const AL_VALUE_KEYS: string[] = [...STANDARD_AUTO_LIABILITY_LIMIT_KEYS, "pip", "basicPip"];

export const UB_VALUE_KEYS = ["eachOccurrence", "aggregate"];

export const WC_VALUE_KEYS = [
  "elEachAccident",
  "elDiseaseEaEmployee",
  "elDiseasePolicyLimit",
];
// CSR-2298 — the LIMIT/DEDUCTIBLE field of each OTHER coverage, mirroring what
// `buildOtherCoverageRows` actually reads before it emits a row. A block also
// carries `status` and per-coverage booleans (`reeferBreakdown`, …); reading
// every property instead counted those as coverage, so a term stripped down to
// `{ status: "active", limit: "", deductible: "", reeferBreakdown: false }`
// read as covered and rendered a blank certificate with no warning.
export const OTHER_COVERAGE_VALUE_KEYS: Record<string, string[]> = {
  cargo: ["limit", "deductible"],
  physicalDamage: ["comprehensiveDeductible", "collisionDeductible"],
  onHook: ["limit", "deductible"],
  trailerInterchange: ["limit", "deductible"],
  nonOwnedTrailerPhysicalDamage: ["limit", "deductible"],
  // Folded into the ACORD Auto Liability box rather than an OTHER row
  // (CSR-1128/CSR-1704), but still real coverage the certificate shows.
  nonTruckingLiability: ["limit"],
  hiredAutoLiability: ["combinedSingleLimit"],
  hiredNonOwnedLiability: ["limit"],
};
export const OTHER_COVERAGE_KEYS = new Set(Object.keys(OTHER_COVERAGE_VALUE_KEYS));
// CSR-1216: a "Renewal Offer" shell is a future-effective policy term that
// carries a COI-eligible status (e.g. "active") but no coverage lines yet.
// Such a shell must never feed COI generation — alone it produces a blank COI,
// and in an aggregate it consumes a carrier letter without rendering any
// coverage. A policy has real COI coverage when at least one ACORD section
// (GL/AL/UB/WC) carries a real limit value, or at least one active OTHER
// coverage carries a value. Mirrors the section/OTHER candidate gating so the
// predicate and the renderer agree.
export function policyHasCoiCoverage(policy: Policy): boolean {
  const coverages =
    (policy.coverages as Record<string, Record<string, unknown> | string | undefined> | null | undefined) ?? {};

  if (hasAnyFieldValue(activeBlock(coverages.generalLiability), GL_VALUE_KEYS)) return true;
  if (hasAnyFieldValue(activeBlock(coverages.autoLiability), AL_VALUE_KEYS)) return true;
  if (hasAnyFieldValue(activeBlock(coverages.umbrella), UB_VALUE_KEYS)) return true;
  if (hasAnyFieldValue(activeBlock(coverages.workersComp), WC_VALUE_KEYS)) return true;

  for (const [key, valueKeys] of Object.entries(OTHER_COVERAGE_VALUE_KEYS)) {
    const block = coverages[key];
    if (!block || !isCoverageActive(block)) continue;
    if (hasAnyFieldValue(block, valueKeys)) return true;
  }

  // CSR-953/CSR-1144: the Physical Damage OTHER row also renders when its
  // deductibles live on per-vehicle data even if the `physicalDamage` block is
  // empty. Mirror that so a genuinely in-force PD policy is never mis-classified
  // as coverage-less.
  if (isCoverageActive(coverages.physicalDamage)) {
    const maxDed = getMaxPhysicalDamageDeductibles({
      vehicles: policy.vehicles as PolicyVehicle[] | null,
      trailers: policy.trailers as PolicyVehicle[] | null,
      coverages: policy.coverages as PolicyCoverages | null,
      physicalDamageVehicles: policy.physicalDamageVehicles as PhysicalDamageVehicleSelection | null,
    });
    if (maxDed.comprehensive !== null || maxDed.collision !== null) return true;
  }

  return false;
}

// CSR-1216 / CSR-2287 — which of the candidate policies actually reach the
// certificate: coverage-less "Renewal Offer" shells are dropped, EXCEPT when
// that would empty the set, in which case they are kept so the agency/producer
// block and audit snapshot still derive from a policy. Shared so the picker's
// mismatch warning predicts the same set the render path builds.
export function selectCoiCertificateCandidates<T extends Policy>(
  candidates: T[],
): T[] {
  const withCoverage = candidates.filter((p) => policyHasCoiCoverage(p));
  return withCoverage.length > 0 ? withCoverage : candidates;
}
